import {yupResolver} from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from "@material-ui/core";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import ImageIcon from "@material-ui/icons/Image";
import CloseIcon from "@material-ui/icons/Close";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import {makeStyles} from "@material-ui/styles";
import BACKGROUND from "assets/img/bg_create_test.png";
import clsx from "clsx";
import InputField from "components/FormFields/InputField";
import {AnswerContent, ExamQuestions, ListQuestion, Question} from "models/managerTest";
import moment from "moment";
import React, {ChangeEvent, Fragment, useEffect, useMemo, useState} from "react";
import {useForm} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import {useSearchParams} from "react-router-dom";
import * as yup from "yup";
import DetailExam from "./DetailExam";
import managerFilesApi from "api/managerFiles";
import Dropzone from "react-dropzone";
import useDebouncedSetter from "hooks/useDebounceSetter";
import ExamStudentPreviewModal from "components/ExamStudentPreviewModal";
import {deduplicateQuestions} from "utils/examPreview";

export interface CreateQuestionProps {
  valueForm: Question | undefined;
  setCreateQuestion: (e: any) => void;
  setValueForm: (e: any) => void;
  listQuestionsAdded: ExamQuestions[];
  setIsPickOldExam: (e: any) => void;
  initialRandomCount?: number;
}

export default function CreateQuestion({
  valueForm,
  setCreateQuestion,
  setValueForm,
  listQuestionsAdded,
  setIsPickOldExam,
  initialRandomCount,
}: CreateQuestionProps) {
  const {t} = useTranslation();
  const initialValue = {
    title: "",
  };

  const createSchema = (
    questions: string[],
    answers: any
    // , isRightAnswers: any
  ) => {
    return yup.object().shape(
      questions.reduce(
        (acc: any, key: string) => ({
          ...acc,
          [key]: yup
            .string()
            .min(1, `${t("managerTest.vuilongnhapcauhoibaithi")}`)
            .max(2000, `Vui lòng nhập tối đa 2000 ký tự`),
          title: yup
            .string()
            .min(1, `${t("managerTest.vuilongnhaptieudebaithi")}`)
            .max(2000, `Vui lòng nhập tối đa 2000 ký tự`),
          ...answers,
          // ...isRightAnswers,
        }),
        {}
      )
    );
  };

  const useStyles = makeStyles(() => ({
    background: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      "& > img": {
        marginBottom: 20,
        objectFit: "cover",
        width: "60%",
      },
      "& >h6": {
        color: "#004673",
        fontSize: 25,
        fontWeight: 600,
        width: "17%",
      },
    },
    content: {
      backgroundColor: "#f3f3f3",
      borderRadius: 8,
      padding: 15,
      boxShadow: "0px 2px 4px rgba(168, 168, 168, 0.4)",
    },
    btn_copy: {
      margin: "0 15px",
    },
    icon: {
      borderRadius: "50%",
      width: 20,
      height: 20,
      boxShadow: "inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)",
      backgroundColor: "#f5f8fa",
      backgroundImage: "linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))",
      "$root.Mui-focusVisible &": {
        outline: "2px auto rgba(19,124,189,.6)",
        outlineOffset: 2,
      },
      "input:hover ~ &": {
        backgroundColor: "#ebf1f5",
      },
      "input:disabled ~ &": {
        boxShadow: "none",
        background: "rgba(206,217,224,.5)",
      },
    },
    checkedIcon: {
      backgroundColor: "#137cbd",
      backgroundImage: "linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))",
      "&:before": {
        display: "block",
        width: 20,
        height: 20,
        backgroundImage: "radial-gradient(#fff,#fff 28%,transparent 32%)",
        content: '""',
      },
      "input:hover ~ &": {
        backgroundColor: "#106ba3",
      },
    },
    btn_submit: {
      background: "linear-gradient(360deg, #399BD9 0%, #83CFFF 172.67%)",
      color: "#fff",
      borderRadius: 10,
      "&:hover": {
        backgroundColor: "#E38825",
      },
    },
    btn_goBack: {
      borderRadius: 10,
      color: "#fff",
      marginRight: 15,
      background: "linear-gradient(0deg, #FD5468 0%, rgba(255, 193, 200, 0.9) 169.32%)",
    },
    btn_add_picture: {
      border: "1px solid #c4c4c4",
      padding: 16,
      borderRadius: 4,
      cursor: "pointer",
      "&:hover": {
        color: "#499cff",
        border: "1px solid #000",
        transition: "all 0.5",
      },
    },
    image_iconClose: {
      position: "absolute",
      right: 10,
      top: 10,
      zIndex: 99,
      backgroundColor: "#FFF",
      borderRadius: "50%",
      width: 25,
      height: 25,
      cursor: "pointer",
    },
    input_confirm_question: {
      border: "none",
      borderBottom: "1px dashed #999",
      background: "transparent",
      width: "45px",
      "&:focus": {
        border: "none",
        borderBottom: "1px dashed #999",
        outline: "none",
      },
      "&:focus-visible": {
        border: "none",
        borderBottom: "1px dashed #999",
        outline: "none",
      },
    },
  }));
  const classes = useStyles();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentStep = Number(searchParams.get("step") || "2");
  const [showDetailExam, setShowDetailExam] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);
  const [valueInputShuffle, setValueInputShuffle] = useState<number | undefined>(initialRandomCount);
  const [questions, setQuestions] = useState<ListQuestion[]>([]);
  const setDebouncedQuestions = useDebouncedSetter(setQuestions, 300);
  const setDebounceTitle = useDebouncedSetter(setValueForm, 300);
  const uniqueQuestionCount = useMemo(() => deduplicateQuestions(questions).length, [questions]);
  useEffect(() => {
    setValueInputShuffle(initialRandomCount);
  }, [initialRandomCount]);
  useEffect(() => {
    const listAdded: any =
      listQuestionsAdded?.map((x, idx) => {
        const pointPerItem = parseFloat(
          (valueForm?.totalPoint / listQuestionsAdded.length).toFixed(1)
        );
        const remainingPoint = parseFloat(
          (valueForm?.totalPoint - pointPerItem * (listQuestionsAdded.length - 1)).toFixed(1)
        );
        return {
          id: Math.random(),
          questionContent: x.question?.content,
          point: idx < listQuestionsAdded.length - 1 ? pointPerItem : remainingPoint,
          // valueForm?.totalPoint % listQuestionsAdded.length === 0
          //   ? valueForm?.totalPoint / listQuestionsAdded.length
          //   : listQuestionsAdded.length - 1 === idx
          //   ? Math.floor(valueForm?.totalPoint / listQuestionsAdded.length) +
          //     (valueForm?.totalPoint % listQuestionsAdded.length)
          //   : Math.floor(valueForm?.totalPoint / listQuestionsAdded.length),
          imageFileIDList:
            (x.question?.imageFileList?.map((y: any) => ({
              id: y.imageFileID,
              url: y.imageFile?.url,
            }))) || [],
          answers:
            x.question?.answers?.map((z: any) => ({
              answerContent: z.content || "",
              isRightAnswer: z.isRightAnswer || false,
            })) || [],
        };
      }) || [];
    const newQuestion: ListQuestion = {
      id: Math.random(),
      questionContent: "",
      point: "",
      imageFileIDList: [],
      answers: [
        {
          answerContent: "",
          isRightAnswer: false,
        },
      ],
    };
    if (listQuestionsAdded.length > 0) {
      setQuestions(listAdded);
    } else {
      setQuestions([newQuestion]);
    }
  }, []);
  const {control, handleSubmit} = useForm({
    defaultValues: useMemo(() => initialValue, [initialValue]),
    resolver: yupResolver(
      createSchema(
        questions.map((x, index) => `questions-${index}`),
        questions
          .reduce((prevValue: any, currentValue: ListQuestion, currentIndex: number) => {
            return [
              ...prevValue,
              ...currentValue?.answers?.map(
                (answer: ListQuestion, index: number) => `answerContent-${currentIndex}-${index}`
              ),
            ];
          }, [])
          .reduce(
            (acc: string[], key: string) => ({
              ...acc,
              [key]: yup
                .string()
                .min(1, `${t("managerTest.vuilongnhapdapancuacauhoi")}`)
                .max(2000, `Vui lòng nhập tối đa 2000 ký tự`),
            }),
            {}
          )
      )
    ),
  });

  const getNormalizedQuestionContent = (content?: string) =>
    (content || "").trim().replace(/\s+/g, " ").toLowerCase();

  const handleSubmitForm = async () => {
    const normalizedContents = questions
      .map((q) => getNormalizedQuestionContent(q.questionContent))
      .filter((content) => content.length > 0);
    const duplicateCount = normalizedContents.length - new Set(normalizedContents).size;
    if (duplicateCount > 0) {
      toast.error(`Có ${duplicateCount} câu hỏi mẹ bị trùng nội dung. Vui lòng kiểm tra lại.`);
      return;
    }

    if (valueForm && valueForm?.totalPoint > 0) {
      const pointQuestion = questions.reduce((x, item) => (x += Number(item.point)), 0).toFixed();
      if (Number(pointQuestion) > valueForm?.totalPoint) {
        toast.error(`${t("managerTest.tongsodiemlonhondiemtongdanhap")}`);
        return;
      } else if (
        pointQuestion === valueForm?.totalPoint &&
        questions.some((item) => item.point === "")
      ) {
        toast.error(`${t("managerTest.tongsodiemlonhondiemtongdanhap")}`);
        return;
      } else if (
        pointQuestion < valueForm?.totalPoint &&
        questions.every((item) => item.point !== "")
      ) {
        toast.error(`${t("managerTest.tongsodiemlonhondiemtongdanhap")}`);
        return;
      }
    }
    if (valueForm?.startDate?.slice(-5)) {
      const currentHours = new Date().getHours();
      const currentMinutes = new Date().getMinutes();
      const currentDay = moment(new Date()).format("YYYY-MM-DD");
      const currentTimeStamp = currentHours * 3600 + currentMinutes * 60;
      const timeClicked =
        Number(valueForm.startDate?.slice(-5).slice(0, 2)) * 3600 +
        Number(valueForm.startDate?.slice(-5).slice(-2)) * 60;
      if (currentTimeStamp > timeClicked && currentDay === valueForm?.startDate?.slice(0, 10)) {
        toast.error(
          `${t("managerTest.vuilongquaylaichonngaygiokiemtralonhonhoacbangngaygiokiemtrahientai")}`
        );
        return;
      }
    }

    if (valueForm?.category === "essay") {
      setShowDetailExam(true);
      setSearchParams({ step: "3" });
      return;
    }

    let hasError = false;
    if (valueForm?.category === "multipleChoice") {
      questions.forEach((x, idx) => {
        if (x.answers.every((y: AnswerContent) => !y.isRightAnswer)) {
          toast.error(`${t("managerTest.vuilongnhapdapancuacauhoi")} ${idx + 1}`);
          hasError = true;
        }
      });
    }

    if (hasError) return;

    setShowDetailExam(true);
    setSearchParams({ step: "3" });
  };
  const handleAddAnswer = (index: number) => {
    const optionsOfQuestion = [...questions];
    if (optionsOfQuestion[index].answers.length < 10) {
      optionsOfQuestion[index].answers.push({
        answerContent: "",
        isRightAnswer: false,
      });
    } else {
      toast.error(`${t("managerTest.toida10cautraloi")}`);
    }
    setQuestions(optionsOfQuestion);
  };

  const handleAddQuestion = () => {
    setQuestions((questions) => [
      ...questions,
      {
        id: Math.random(),
        questionContent: "",
        point: "",
        imageFileIDList: [],
        answers: [
          {
            answerContent: "",
            isRightAnswer: false,
          },
        ],
      },
    ]);
  };
  const handleCopyQuestion = (index: number) => {
    const qs = [...questions];
    let newQs = qs[index];
    const isDuplicate = questions.some(
      (item, idx) =>
        idx !== index && item.questionContent?.trim() === newQs.questionContent?.trim()
    );
    if (isDuplicate) {
      toast.error("Câu hỏi này đã tồn tại trong đề");
      return;
    }
    setQuestions([
      ...questions,
      {
        id: Math.random(),
        questionContent: newQs.questionContent,
        point: newQs.point,
        imageFileIDList: newQs.imageFileIDList,
        answers: newQs.answers.map((answer: AnswerContent) => ({
          answerContent: answer.answerContent,
          isRightAnswer: answer.isRightAnswer,
        })),
      },
    ]);
  };
  const handleDeleteQuestion = (index: number) => {
    const qs = [...questions];
    if (questions.length > 1) {
      qs.splice(index, 1);
    }
    setQuestions(qs);
  };
  const handleDeleteAnswer = (i: number, j: number) => {
    const optionsOfQuestion = [...questions];
    if (optionsOfQuestion[i].answers.length > 1) {
      optionsOfQuestion[i].answers.splice(j, 1);
      setQuestions(optionsOfQuestion);
    }
  };

  const handleChangeOptionValue = (value: string, i: number, j: number) => {
    const optionsOfQuestion = [...questions];
    optionsOfQuestion[i].answers[j].answerContent = value;
    setDebouncedQuestions(optionsOfQuestion);
  };

  const handleChangeQuestionValue = (value: string, i: number) => {
    const optionsOfQuestion = [...questions];
    optionsOfQuestion[i].questionContent = value;
    setDebouncedQuestions(optionsOfQuestion);
  };

  const handleChangePointValue = (value: string, i: number) => {
    const optionsOfQuestion = [...questions];
    optionsOfQuestion[i].point = value;
    setDebouncedQuestions(optionsOfQuestion);
  };

  const handleChangeTitleQuestion = (value: string) => {
    setDebounceTitle({...valueForm, title: value});
  };

  const handleChangeRadioAnswer = (
    event: React.ChangeEvent<HTMLInputElement>,
    i: number,
    j: number
  ) => {
    setIsRightAnswer(i);
    const optionsOfQuestion = [...questions];
    optionsOfQuestion[i].answers[j].isRightAnswer = event.target.checked;
    setDebouncedQuestions(optionsOfQuestion);
  };

  const setIsRightAnswer = (i: number) => {
    let qs = [...questions];
    const answerRight = qs[i].answers;

    for (let j = 0; j < answerRight.length; j++) {
      answerRight[j].isRightAnswer = false;
    }
    setDebouncedQuestions(qs);
  };

  const handleAddPicture = async (acceptedFiles: any, i: number) => {
    const formData = new FormData();
    try {
      acceptedFiles.forEach((acceptedFile: any) => {
        formData.append("attachments", acceptedFile);
      });
      const data = await managerFilesApi.uploadFiles(formData);
      const optionsOfQuestion = [...questions];
      optionsOfQuestion[i].imageFileIDList = [
        ...optionsOfQuestion[i].imageFileIDList,
        ...data.body?.attachmentList,
      ];
      setQuestions(optionsOfQuestion);
    } catch (error) {
      console.log(error);
    }
  };

  const handleDeleteImage = (indexArr: number, idxImage: number) => {
    const optionsOfQuestion = [...questions];
    optionsOfQuestion[indexArr].imageFileIDList.splice(idxImage, 1);
    setQuestions(optionsOfQuestion);
  };
  return (
    <>
      <Box style={{ paddingBottom: 80 }}>
        {currentStep >= 3 || showDetailExam ? (
          <DetailExam
            listQuestion={questions}
            valueForm={valueForm}
            setShowDetailExam={(val: boolean) => {
              setShowDetailExam(val);
              setSearchParams({ step: val ? "3" : "2" });
            }}
            valueInputShuffle={valueInputShuffle}
          />
        ) : (
          <>
            <form onSubmit={handleSubmit(handleSubmitForm)}>
              <Box className={classes.background}>
                <Typography variant="subtitle2">
                  {t("managerTest.taodethi").toUpperCase()}
                </Typography>
                <img src={BACKGROUND} alt="BACKGROUND" />
              </Box>
              <InputField
                name="title"
                label={t("managerTest.nhaptieudebaithi").toUpperCase()}
                control={control}
                rows={2}
                multiline
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  handleChangeTitleQuestion(event.target.value)
                }
              />

              {questions.map((question, index) => (
                <Box className={classes.content} mt={2} key={index}>
                  <Box display="flex" alignItems="center" key={index}>
                    <InputField
                      control={control}
                      name={`questions-${index}`}
                      label={`${t("managerTest.cau")} ${index + 1}`}
                      defaultValue={question.questionContent}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        handleChangeQuestionValue(event.target.value, index)
                      }
                      isShowLabel={true}
                    />
                    <Box width="10%" ml={3}>
                      <InputField
                        name={`point-${index}`}
                        control={control}
                        defaultValue={question.point}
                        label={t("managerTest.diem")}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          handleChangePointValue(event.target.value, index)
                        }
                      />
                    </Box>
                    <Dropzone onDrop={(event: any) => handleAddPicture(event, index)}>
                      {({getRootProps, getInputProps}) => (
                        <Box width="15%" ml={3} {...getRootProps()}>
                          <label htmlFor="button-add-picture" className={classes.btn_add_picture}>
                            {t("managerTest.themanh")}
                          </label>
                          <input {...getInputProps()} accept="image/*" />
                        </Box>
                      )}
                    </Dropzone>
                  </Box>
                  {question?.imageFileIDList?.length > 0 && (
                    <Box
                      display="grid"
                      gridTemplateColumns={
                        question?.imageFileIDList?.length === 1 ? "1fr" : "1fr 1fr"
                      }
                      gridGap={10}
                      my="10px"
                    >
                      {question.imageFileIDList.map((x: any, idxImage: number) => {
                        return (
                          <Box key={x.id} position="relative">
                            <Box
                              className={classes.image_iconClose}
                              onClick={() => handleDeleteImage(index, idxImage)}
                            >
                              <CloseIcon />
                            </Box>
                            <img
                              style={{
                                width: "100%",
                                height: "fit-content",
                                objectFit: "contain",
                                maxHeight: "450px",
                              }}
                              src={x.url}
                              alt={x}
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                  <RadioGroup name="radios-button" aria-label="radios-button">
                    {question.answers.map((answer: AnswerContent, index2: number) => (
                      <Fragment key={index2}>
                        <Box alignItems="center" display="flex" justifyContent="space-between">
                          {/* Câu hỏi tự luận */}
                          {valueForm?.category === "essay" ? (
                            <Box mt={2} width="100%">
                              <InputField
                                label={t("managerTest.dapan")}
                                name={`answerContent-${index}-${index2}`}
                                defaultValue={answer.answerContent}
                                control={control}
                                rows={3}
                                multiline
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                  handleChangeOptionValue(event.target.value, index, index2)
                                }
                              />
                            </Box>
                          ) : (
                            <Box
                              justifyContent="flex-end"
                              display="flex"
                              alignItems="center"
                              mt={1}
                              width="60%"
                            >
                              {/* Câu hỏi trắc nghiệm*/}
                              <FormControlLabel
                                control={
                                  <Radio
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                      handleChangeRadioAnswer(event, index, index2)
                                    }
                                    disableRipple
                                    checked={answer.isRightAnswer}
                                    value={`radio-${index}-${index2}`}
                                    name={`radio-${index}-${index2}`}
                                    icon={<span className={classes.icon} />}
                                    checkedIcon={
                                      <span className={clsx(classes.icon, classes.checkedIcon)} />
                                    }
                                    style={{marginTop: 15}}
                                  />
                                }
                                label=""
                              />

                              <InputField
                                label={`${t("managerTest.dapan")} ${index2 + 1}`}
                                name={`answerContent-${index}-${index2}`}
                                defaultValue={answer.answerContent}
                                control={control}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                  handleChangeOptionValue(event.target.value, index, index2)
                                }
                              />
                            </Box>
                          )}
                          {valueForm?.category === "multipleChoice" && (
                            <Box>
                              <Tooltip title={`${t("managerTest.xoadapan")} ${index2 + 1}`}>
                                <IconButton onClick={() => handleDeleteAnswer(index, index2)}>
                                  <CloseIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>
                      </Fragment>
                    ))}
                  </RadioGroup>
                  {valueForm?.category === "multipleChoice" && (
                    <Box mt={2}>
                      <Typography
                        variant="body2"
                        component="span"
                        onClick={() => handleAddAnswer(index)}
                        color="primary"
                        style={{fontSize: 16, cursor: "pointer", marginLeft: 5}}
                      >
                        {t("managerTest.them")}
                      </Typography>
                    </Box>
                  )}
                  <Box textAlign="right" borderTop="1px solid #AAAAAA" mt={1}>
                    <Box mt={1}>
                      <Button
                        startIcon={<AddCircleOutlineIcon />}
                        color="primary"
                        onClick={handleAddQuestion}
                      >
                        {t("managerTest.them")}
                      </Button>

                      <Button
                        startIcon={<AddCircleOutlineIcon />}
                        color="primary"
                        onClick={() => handleCopyQuestion(index)}
                        className={classes.btn_copy}
                      >
                        {t("managerTest.saochep")}
                      </Button>

                      <Button
                        startIcon={<DeleteOutlineIcon />}
                        color="secondary"
                        onClick={() => handleDeleteQuestion(index)}
                      >
                        {t("button.delete")}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              ))}

              <Box display="flex" justifyContent={"space-between"} alignItems={"center"}>
                <Box>
                  <Typography variant="subtitle2">
                    Ngẫu nhiên{" "}
                    <input
                      className={classes.input_confirm_question}
                      type="number"
                      value={valueInputShuffle ?? ""}
                      onChange={(e) => {
                        const rawValue = e.target.value;
                        if (rawValue === "") {
                          setValueInputShuffle(undefined as any);
                          return;
                        }
                        const parsedValue = Number(rawValue);
                        setValueInputShuffle(Number.isNaN(parsedValue) ? undefined : (parsedValue as any));
                      }}
                    />{" "}
                    /{uniqueQuestionCount} câu
                  </Typography>
                  {(valueInputShuffle ?? 0) > uniqueQuestionCount && (
                    <Box style={{color: "red"}}>Vui lòng nhập đúng số lượng câu hỏi</Box>
                  )}
                  <Box mt={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      disabled={questions.length === 0}
                      onClick={() => setOpenPreview(true)}
                    >
                      Xem thử như học viên
                    </Button>
                  </Box>
                </Box>

                <Box
                  style={{
                    position: "fixed",
                    bottom: 0,
                    right: 24,
                    left: 280,
                    backgroundColor: "#fff",
                    padding: "12px 24px",
                    boxShadow: "0px -4px 12px rgba(0, 0, 0, 0.15)",
                    zIndex: 1200,
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    borderTopLeftRadius: 8,
                    borderTopRightRadius: 8,
                    borderTop: "1px solid #E0E0E0",
                  }}
                >
                  <Button
                    onClick={() => {
                      setCreateQuestion(false);
                      if (listQuestionsAdded.length > 0) {
                        setIsPickOldExam(true);
                      }
                    }}
                    className={classes.btn_goBack}
                  >
                    {t("button.goback")}
                  </Button>

                  <Button type="submit" className={classes.btn_submit}>
                    {t("managerTest.xong")}
                  </Button>
                </Box>
              </Box>
            </form>
          </>
        )}
      </Box>
      {/* )} */}
      <ExamStudentPreviewModal
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        questions={questions}
        displayCount={valueInputShuffle}
        examTitle={valueForm?.title}
        isEssay={valueForm?.category === "essay"}
      />
    </>
  );
}
